import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Success Animation Component
const SuccessAnimation = ({ onClose, language }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-800/95 backdrop-blur-sm rounded-2xl overflow-hidden">
      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full animate-float-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              backgroundColor: i % 2 === 0 ? '#ec4899' : '#3b82f6',
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>
      
      {/* Success content */}
      <div className="relative text-center px-8">
        {/* Animated checkmark circle */}
        <div className="relative mx-auto mb-6">
          {/* Outer glow ring */}
          <div className="absolute inset-0 w-24 h-24 mx-auto bg-gradient-to-r from-pink-500 to-blue-500 rounded-full animate-ping opacity-20"></div>
          
          {/* Main circle */}
          <div className="relative w-24 h-24 mx-auto bg-gradient-to-br from-pink-500 to-blue-600 rounded-full flex items-center justify-center animate-success-bounce shadow-2xl shadow-pink-500/50">
            <CheckCircle className="w-12 h-12 text-white animate-success-check" strokeWidth={2.5} />
          </div>
        </div>
        
        {/* Text */}
        <h3 className="text-2xl font-bold text-white mb-2 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          {language === 'ro' ? 'Mesaj Trimis!' : 'Message Sent!'}
        </h3>
        <p className="text-gray-400 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
          {language === 'ro' 
            ? 'Vă vom contacta în curând.' 
            : "We'll get back to you soon."}
        </p>
        
        {/* Progress bar */}
        <div className="mt-6 w-48 h-1 bg-slate-700 rounded-full mx-auto overflow-hidden">
          <div className="h-full bg-gradient-to-r from-pink-500 to-blue-500 rounded-full animate-progress-bar"></div>
        </div>
      </div>
    </div>
  );
};

export const ContactSection = () => {
  const { toast } = useToast();
  const { language } = useLanguage();
  const t = translations[language].contact;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowSuccess(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-slate-900 relative overflow-hidden" data-testid="contact-section">
      {/* Background Effect with Pink */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-pink-500/30 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4">{t.title}</h2>
            <p className="text-xl text-gray-400">{t.subtitle}</p>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-pink-500 to-transparent mx-auto mt-6"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8 animate-fade-in" style={{ animationDelay: '200ms' }}>
              <div>
                <h3 className="text-2xl font-bold text-white mb-6">{t.formTitle}</h3>
                <p className="text-gray-400 text-lg mb-8">
                  {t.formDescription}
                </p>
              </div>

              <div className="space-y-6">
                <div className="group flex items-start space-x-4 transform hover:translate-x-2 transition-transform duration-300">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-600/20 to-pink-600/20 rounded-lg flex items-center justify-center border border-pink-500/30 group-hover:border-pink-500/60 transition-colors duration-300">
                    <Mail className="text-pink-400" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{t.email}</h4>
                    <a href="mailto:contact@worldwideinfluence.com" className="text-gray-400 hover:text-pink-400 transition-colors duration-300">
                      contact@worldwideinfluence.com
                    </a>
                  </div>
                </div>

                <div className="group flex items-start space-x-4 transform hover:translate-x-2 transition-transform duration-300">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-600/20 to-pink-600/20 rounded-lg flex items-center justify-center border border-pink-500/30 group-hover:border-pink-500/60 transition-colors duration-300">
                    <Phone className="text-pink-400" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{t.phone}</h4>
                    <a href="tel:+1234567890" className="text-gray-400 hover:text-pink-400 transition-colors duration-300">
                      +1 (234) 567-890
                    </a>
                  </div>
                </div>

                <div className="group flex items-start space-x-4 transform hover:translate-x-2 transition-transform duration-300">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-600/20 to-pink-600/20 rounded-lg flex items-center justify-center border border-pink-500/30 group-hover:border-pink-500/60 transition-colors duration-300">
                    <MapPin className="text-pink-400" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{t.location}</h4>
                    <p className="text-gray-400">{t.locationText}<br />{t.locationSub}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="relative animate-fade-in" style={{ animationDelay: '400ms' }}>
              {/* Pink Glow Behind Form */}
              <div className="absolute -inset-4 bg-gradient-to-br from-pink-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-50"></div>
              
              <div className="relative bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border border-pink-500/30 shadow-2xl">
                {/* Success Animation Overlay */}
                {showSuccess && (
                  <SuccessAnimation 
                    onClose={() => setShowSuccess(false)} 
                    language={language}
                  />
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      {t.form.nameRequired}
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-pink-500 focus:ring-pink-500 transition-colors duration-300"
                      placeholder={t.form.namePlaceholder}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      {t.form.emailRequired}
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-pink-500 focus:ring-pink-500 transition-colors duration-300"
                      placeholder={t.form.emailPlaceholder}
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                      {t.form.phone}
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-pink-500 focus:ring-pink-500 transition-colors duration-300"
                      placeholder={t.form.phonePlaceholder}
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      {t.form.messageRequired}
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-pink-500 focus:ring-pink-500 resize-none transition-colors duration-300"
                      placeholder={t.form.messagePlaceholder}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-pink-600 hover:from-blue-700 hover:to-pink-700 text-white py-6 text-lg font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-pink-500/50"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? (
                      t.form.sending
                    ) : (
                      <span className="flex items-center justify-center">
                        {t.form.submit}
                        <Send className="ml-2" size={20} />
                      </span>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
